import type { CustomAxiosError } from './IUseAxios'
import { ErrorCode } from '@nami/domain/enums'

import { apiConfig } from '../../classes/ApiConfigManager'
import { useLogger } from '../useLogger'

/**
 * Handle 401 unauthorized error
 */
const handleUnauthorizedError = (): void => {
    const homeUrl = apiConfig.getHomeUrl()
    if (!homeUrl) {
        return
    }

    if (homeUrl === 'close') {
        window.close()
    }
    else {
        location.href = homeUrl
    }
}

/**
 * Handle timeout error
 */
const handleTimeoutError = (error: CustomAxiosError): void => {
    const logger = useLogger()

    const timeoutError = {
        message: 'API_REQUEST_TIMEOUT',
        requestUrl: error.config.url,
        requestMethod: error.config.method,
        timeout: error.config.timeout,
        loginName: apiConfig.getLoginName(),
        currentPath: window.location.pathname,
        userAgent: navigator.userAgent,
    }

    logger.logError('API_REQUEST_TIMEOUT', timeoutError)
}

/**
 * Log error details
 */
const logErrorDetails = (error: CustomAxiosError): void => {
    const logger = useLogger()

    const errorInfo = {
        message: error.message,
        code: error.code,
        request: {
            url: error.config?.url,
            method: error.config?.method,
            headers: error.config?.headers,
            data: error.config?.data,
            timeout: error.config?.timeout,
        },
        response: {
            status: error.response?.status,
            statusText: error.response?.statusText,
            headers: error.response?.headers,
            data: error.response?.data,
        },
        userInfo: {
            loginName: apiConfig.getLoginName(),
            currentPath: window.location.pathname,
            userAgent: navigator.userAgent,
        },
        timestamp: new Date().toISOString(),
    }

    logger.logDebug(JSON.stringify(errorInfo))
}

/**
 * Redirect to error page
 */
const redirectToErrorPage = (errorCode: string): void => {
    apiConfig.getRouter()?.push({ path: '/payment/error', query: { errorCode } }).then()
}

/**
 * Create error handler function
 */
export const createErrorHandler = () => (error: CustomAxiosError): Promise<never> => {
    // If skip interceptor is set, directly throw error
    if (error.config?.skipInterceptor) {
        return Promise.reject(error)
    }

    // Handle 401 unauthorized error
    if (error.response?.status === 401) {
        handleUnauthorizedError()
    }

    // Handle timeout error
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        handleTimeoutError(error)
    }

    // Get error code
    let errorCode = error.response?.data?.errorCode
    if (!errorCode) {
        errorCode = ErrorCode.Unknown.toString()
    }

    // If there's a response status, log error and redirect to error page
    if (error.response?.status) {
        logErrorDetails(error)
        redirectToErrorPage(errorCode)
    }

    throw error
}
